import React, { useEffect, useState, useRef } from "react";
import { ChevronDown, Download, BarChart3, Activity } from "lucide-react";
import { motion } from "framer-motion";
import {
  ComposedChart,
  Line,
  XAxis,
  Bar,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import {
  Spin,
  DatePicker,
  Dropdown,
  ConfigProvider,
  theme as antTheme,
} from "antd";
import { SwapOutlined } from "@ant-design/icons";
import moment from "moment";
import { CSVLink } from "react-csv";
import * as htmlToImage from "dom-to-image-more";
import { toast } from "react-toastify";

// Context & Services
import { useTheme } from "../context/ThemeContext";
import { get, post } from "../Libraries/Ajax/HttpService";
import { useDistrict } from "../utils/DistrictContext";
import ExamplesWithDistrict from "../pages/multipleselectDropdownagg";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export default function AggregateUsageTab() {
  const { theme, colors } = useTheme();

  // --- Core States ---
  const [loading, setLoading] = useState(false);
  const [aggregateData, setAggregateData] = useState([]);
  const [categoryChartsData, setCategoryChartsData] = useState([]);
  const [myData, setMyData] = useState([]);
  const [callData, setCallData] = useState([]);

  // UNIFIED TOGGLE STATE
  const [categoryEnabled, setCategoryEnabled] = useState(false);
  const [userColors, setUserColors] = useState([]);

  // --- Filter States ---
  const [dateRange, setDateRange] = useState("Past Month");
  const [intervalLabel, setIntervalLabel] = useState("Day");
  const [slvalue, setSlvalue] = useState(1);
  const selectedDistrict = useDistrict();

  // --- Custom Date States ---
  const [parameterone, setParameterone] = useState(null);
  const [parametertwo, setParametertwo] = useState(null);

  // Stores selected category codes per account.
  const [selectedAccountCodes, setSelectedAccountCodes] = useState({});
  const tabName = "Aggregate_Usage";
  const [chartType, setChartType] = useState("line");
  const chartContainerRefs = useRef([]);
  const disabledDate = (current) => {
    // Can not select days after today
    return current && current > moment().endOf("day");
  };

  const handleRangeClick = (val) => {
    setDateRange(val);
    // Clear custom dates when clicking a preset
    setParameterone(null);
    setParametertwo(null);
  };
  // 1. Fetch User Color Preferences
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("MCData"));
    if (user?.userid) {
      get(`apis/user_color?user_id=${user.userid}`).then((res) => {
        setUserColors(res.data.color || []);
      });
    }
  }, [selectedDistrict]);

  // 2. Toggle Category Logic
  const toggleCategorySelection = (accountName, code, allCodesForAccount) => {
    setSelectedAccountCodes((prev) => {
      const currentCodes = prev[accountName] || allCodesForAccount;
      const isSelected = currentCodes.includes(code);

      return {
        ...prev,
        [accountName]: isSelected
          ? currentCodes.filter((c) => c !== code)
          : [...currentCodes, code],
      };
    });
  };

  // 3. Interval Logic Sync
  useEffect(() => {
    if (["Past Month", "Past 3 Months", "Past Year"].includes(dateRange)) {
      setIntervalLabel("Week");
    } else if (dateRange === "Past Day") {
      setIntervalLabel("Hour");
    } else if (dateRange === "Custom") {
      if (parameterone && parametertwo) {
        const diffDays = moment(parametertwo).diff(
          moment(parameterone),
          "days",
        );
        setIntervalLabel(diffDays > 7 ? "Week" : "Day");
      }
    } else {
      setIntervalLabel("Day");
    }
  }, [dateRange, parameterone, parametertwo]);

  // 4. Dropdown Sync
  const handleDropdownCallback = (childData, selectedRes) => {
    const categories =
      childData?.data?.data?.categories?.selected_categories || [];
    setCallData(categories);
    if (selectedRes) setMyData(selectedRes);
  };

  // 5. Sidebar Interaction Toggle
  const toggleAggregateAccountSelection = (name) => {
    setMyData((prevData) => {
      const decodedName = decodeURIComponent(name);
      const isSelected = prevData.some(
        (item) => decodeURIComponent(item) === decodedName,
      );
      return isSelected
        ? prevData.filter((item) => decodeURIComponent(item) !== decodedName)
        : [...prevData, name];
    });
  };

  // 6. Custom Date Handler
  const onCustomRangeChange = (dates, dateStrings) => {
    if (dateStrings && dateStrings[0] && dateStrings[1]) {
      setParameterone(dateStrings[0]);
      setParametertwo(dateStrings[1]);
    } else {
      setParameterone(null);
      setParametertwo(null);
    }
  };

  // 7. Data Fetching
  useEffect(() => {
    if (
      myData.length === 0 ||
      (dateRange === "Custom" && (!parameterone || !parametertwo))
    ) {
      setAggregateData([]);
      setCategoryChartsData([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    let commonParams = `option=${intervalLabel}&range_option=${dateRange}&interval=${slvalue}`;
    if (dateRange === "Custom") {
      commonParams += `&start=${parameterone}&end=${parametertwo}`;
    }

    if (!categoryEnabled) {
      setCategoryChartsData([]);
      get(
        `apis/water/aggregate/account?${commonParams}&selected=${JSON.stringify(myData)}`,
        { signal: controller.signal },
      )
        .then((res) => {
          const raw = res?.data?.data?.water_flow || [];
          const formatted = raw.map((item) => {
            const meterData = Object.fromEntries(
              item.map((obj) => [obj.overided_name, obj.flow]),
            );
            return { date: item[0]?.flow_date_check, ...meterData };
          });
          setAggregateData(formatted);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setAggregateData([]);
      const decodedMyData = myData.map((name) => decodeURIComponent(name));
      const categoryMap = {};

      callData.forEach((acc) => {
        if (decodedMyData.includes(acc.Overide_acc_name)) {
          acc.category.forEach((cat) => {
            const activeCodes = selectedAccountCodes[acc.Overide_acc_name];
            const isCodeActive =
              activeCodes === undefined ? true : activeCodes.includes(cat.code);

            if (isCodeActive) {
              if (!categoryMap[cat.code]) {
                categoryMap[cat.code] = { code: cat.code, accounts: [] };
              }
              if (
                !categoryMap[cat.code].accounts.includes(acc.Overide_acc_name)
              ) {
                categoryMap[cat.code].accounts.push(acc.Overide_acc_name);
              }
            }
          });
        }
      });

      const paramsDatas = Object.values(categoryMap).sort((a, b) =>
        a.code.localeCompare(b.code),
      );

      if (paramsDatas.length === 0) {
        setCategoryChartsData([]);
        setLoading(false);
        return;
      }

      post(`apis/water/aggregate/category?${commonParams}`, paramsDatas, {
        signal: controller.signal,
      })
        .then((res) => {
          const aggregatedData = res?.data?.data?.water_flow || {};
          const charts = Object.keys(aggregatedData).map((catCode) => {
            return aggregatedData[catCode].map((arr) => {
              const categoryLabel = arr[0].code_name;
              const resObj = {
                date: arr[0].flow_date_check,
                catName: arr[0].code_name,
              };
              arr.forEach((obj) => {
                // resObj[obj.overided_name] = obj.flow;
                const uniqueLegendKey = `${categoryLabel} (${obj.overided_name})`;
                resObj[uniqueLegendKey] = obj.flow;
              });
              return resObj;
            });
          });
          setCategoryChartsData(charts);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }

    return () => controller.abort();
  }, [
    intervalLabel,
    slvalue,
    dateRange,
    myData,
    categoryEnabled,
    selectedDistrict,
    callData,
    parameterone,
    parametertwo,
    selectedAccountCodes,
  ]);

  // 8. Download Handler
  const downloadImage = async (index = 0) => {
    if (myData.length === 0) {
      toast.info("Please select accounts before downloading.");
      return;
    }
    const targetRef = chartContainerRefs.current[index];
    if (!targetRef) return;
    try {
      const dataUrl = await htmlToImage.toPng(targetRef, {
        backgroundColor: colors.cardBg,
        cacheBust: true,
      });
      const link = document.createElement("a");
      link.download = `Aggregate_Usage_Chart${index + 1}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      toast.error("Failed to download image.");
    }
  };

  const downloadMenuOptions = [
    {
      key: "1",
      label: <span onClick={() => downloadImage(0)}>Download As PNG</span>,
    },
    { type: "divider" },
    {
      key: "2",
      label: (
        <CSVLink
          data={categoryEnabled ? categoryChartsData.flat() : aggregateData}
          filename={`${tabName}_${selectedDistrict?.selectedDistrict}.csv`}
        >
          Download As CSV
        </CSVLink>
      ),
    },
  ];

  const chartsToRender = categoryEnabled ? categoryChartsData : [aggregateData];

  return (
    <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 mb-8">
      {/* Sidebar */}
      <motion.div
        className="w-full lg:w-75 rounded-lg border p-4 shrink-0"
        style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setCategoryEnabled(!categoryEnabled)}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            style={{
              backgroundColor: categoryEnabled
                ? colors.primary
                : `${colors.primary}20`,
            }}
          >
            <span
              className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              style={{
                transform: categoryEnabled
                  ? "translateX(1.5rem)"
                  : "translateX(0.25rem)",
              }}
            />
          </button>
          <span
            className="text-xs sm:text-sm font-medium"
            style={{ color: colors.text }}
          >
            Category
          </span>
        </div>

        <div className="space-y-2">
          {callData.map((account, index) => {
            const decodedAccName = decodeURIComponent(account.Overide_acc_name);
            const isSelected = myData.some(
              (item) => decodeURIComponent(item) === decodedAccName,
            );

            return (
              <div key={index} className="space-y-1">
                <button
                  onClick={() =>
                    toggleAggregateAccountSelection(account.Overide_acc_name)
                  }
                  className="w-full text-left px-3 py-2 rounded-lg transition-all font-semibold text-sm"
                  style={{
                    backgroundColor: isSelected
                      ? `${colors.primary}20`
                      : "transparent",
                    color: isSelected ? colors.primary : colors.text,
                  }}
                >
                  {account.Overide_acc_name}
                </button>

                {categoryEnabled &&
                  isSelected &&
                  account.category?.map((cat, idx) => {
                    const activeCodes =
                      selectedAccountCodes[account.Overide_acc_name];
                    const isCatActive =
                      !activeCodes || activeCodes.includes(cat.code);
                    const allCodes = account.category.map((c) => c.code);

                    return (
                      <div
                        key={idx}
                        onClick={() =>
                          toggleCategorySelection(
                            account.Overide_acc_name,
                            cat.code,
                            allCodes,
                          )
                        }
                        className="ml-4 px-2 py-1 text-sm cursor-pointer flex items-center gap-2 hover:opacity-80 transition-colors"
                        style={{
                          color: isCatActive
                            ? colors.primary
                            : colors.textSecondary,
                        }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{
                            backgroundColor: isCatActive
                              ? colors.primary
                              : colors.border,
                          }}
                        />
                        {cat.code_name}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      </motion.div>

      <div className="flex-1 space-y-4 sm:space-y-6">
        <motion.div
          className="backdrop-blur-sm rounded-xl border p-4 sm:p-6"
          style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center justify-between gap-4 max-[1900px]:flex-wrap">
            <div className="w-[400px] shrink-0 max-[1900px]:w-full">
              <label
                className="text-xs mb-2 block"
                style={{ color: colors.primary }}
              >
                Accounts
              </label>
              <div className="mb-0">
                <ExamplesWithDistrict
                  parentCallback={handleDropdownCallback}
                  elementValue={myData}
                  selectedDistrict={selectedDistrict?.selectedDistrict}
                />
              </div>
            </div>

            <div className="w-full flex items-center gap-2 max-[1900px]:w-full max-[767px]:flex-wrap">
              <div className="flex-1">
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Range | Custom | Interval
                </label>
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border w-full cursor-pointer max-[1900px]:w-full justify-between flex-wrap"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  }}
                >
                  {/* --- SECTION 1: RANGE PRESETS --- */}
                  <div className="flex items-center gap-1 cursor-pointer">
                    {[
                      { label: "D", value: "Past Day" },
                      { label: "W", value: "Past Week" },
                      { label: "1M", value: "Past Month" },
                      { label: "3M", value: "Past 3 Months" },
                      { label: "YTD", value: "YTD" },
                      { label: "1Y", value: "Past Year" },
                    ].map((tp) => {
                      // Highlight logic for YTD since it technically sets "Custom" range
                      const isYTDActive =
                        tp.value === "YTD" &&
                        parameterone ===
                          moment().startOf("year").format("YYYY-MM-DD") &&
                        parametertwo === moment().format("YYYY-MM-DD");

                      const isActive = dateRange === tp.value || isYTDActive;

                      return (
                        <button
                          key={tp.value}
                          onClick={() => {
                            if (tp.value === "YTD") {
                              const startOfYear = moment()
                                .startOf("year")
                                .format("YYYY-MM-DD");
                              const today = moment().format("YYYY-MM-DD");
                              setParameterone(startOfYear);
                              setParametertwo(today);
                              setDateRange("Custom"); // API expects "Custom" to use start/end
                              setIntervalLabel("Week");
                            } else {
                              handleRangeClick(tp.value);
                            }
                          }}
                          className="px-2 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer"
                          style={{
                            backgroundColor: isActive
                              ? `${colors.primary}20`
                              : "transparent",
                            color: isActive
                              ? colors.primary
                              : colors.textSecondary,
                            border: isActive
                              ? `1px solid ${colors.primary}40`
                              : "1px solid transparent",
                          }}
                        >
                          {tp.label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Vertical Separator 2 */}
                  <div
                    className="h-6 w-[1px] mx-1"
                    style={{ backgroundColor: colors.border }}
                  />

                  {/* --- SECTION 3: CALENDAR (STRICT DAYJS FIX) --- */}
                  <ConfigProvider
                    theme={{
                      algorithm:
                        theme === "white"
                          ? antTheme.defaultAlgorithm
                          : antTheme.darkAlgorithm,
                      token: {
                        colorBgContainer:
                          theme === "white" ? "#ffffff" : colors.cardBg,
                        colorBorder: colors.border,
                        colorText: colors.text,
                        colorTextPlaceholder: colors.textSecondary,
                        // FORCE ICON COLORS
                        colorIcon:
                          theme === "white"
                            ? "rgba(0, 0, 0, 0.45)"
                            : "rgba(255, 255, 255, 0.65)",
                        colorIconHover: colors.primary,
                        controlHeight: 42,
                        borderRadius: 8,
                      },
                    }}
                  >
                    <RangePicker
                      // Using dayjs with strict parsing to fix the 2044 jump
                      value={[
                        parameterone ? dayjs(parameterone, "YYYY-MM-DD") : null,
                        parametertwo ? dayjs(parametertwo, "YYYY-MM-DD") : null,
                      ]}
                      onChange={(dates, dateStrings) => {
                        // Ensure dateStrings[0] is exactly "YYYY-MM-DD"
                        if (dateStrings[0] && dateStrings[1]) {
                          setParameterone(dateStrings[0]);
                          setParametertwo(dateStrings[1]);
                          setDateRange("Custom");
                        }
                      }}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                      placeholder={["Start", "End"]}
                      variant="borderless"
                      separator={
                        <SwapOutlined style={{ color: colors.textSecondary }} />
                      }
                      className="custom-range-picker cursor-pointer"
                      style={{ width: "250px" }}
                      classNames={{
                          popup: {
                            root: `mizu-calendar-popup calendar-${theme}`,
                          },
                        }}
                    />
                  </ConfigProvider>
                  {/* Vertical Separator 1 */}
                  <div
                    className="h-6 w-[1px] mx-1"
                    style={{ backgroundColor: colors.border }}
                  />

                  {/* --- SECTION 2: INTERVAL SELECTOR --- */}
                  <div className="flex items-center gap-1">
                    {["Hour", "Day", "Week"].map((intvl) => {
                      const isPastDay = dateRange === "Past Day";
                      const isPastWeek = dateRange === "Past Week";

                      // Logic to hide intervals that don't apply
                      if (intvl === "Day" && isPastDay) return null;
                      if (intvl === "Week" && (isPastDay || isPastWeek))
                        return null;
                      // if (
                      //   intvl === "Month" &&
                      //   !["Past Year", "Custom"].includes(dateRange)
                      // )
                      //   return null;

                      return (
                        <button
                          key={intvl}
                          onClick={() => setIntervalLabel(intvl)}
                          className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all cursor-pointer"
                          style={{
                            backgroundColor:
                              intervalLabel === intvl
                                ? colors.primary
                                : "transparent",
                            color:
                              intervalLabel === intvl
                                ? "#fff"
                                : colors.textSecondary,
                          }}
                        >
                          {intvl}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:pt-6 pb-0.5">
                <button
                  onClick={() =>
                    setChartType(chartType === "line" ? "bar" : "line")
                  }
                  className="flex items-center justify-center w-[42px] h-[42px] rounded-lg border transition-all hover:shadow-md"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.primary,
                  }}
                  title={
                    chartType === "line"
                      ? "Switch to Stacked Bar Chart"
                      : "Switch to Line Chart"
                  }
                >
                  {chartType === "line" ? (
                    <BarChart3 size={20} />
                  ) : (
                    <Activity size={20} />
                  )}
                </button>
              </div>
              {/* Main Download - ONLY SHOW IF CATEGORY IS OFF */}
              {!categoryEnabled && (
                <div className="flex sm:items-end sm:pt-6 h-full pb-0.5 justify-end lg:justify-start">
                  <Dropdown
                    menu={{ items: downloadMenuOptions }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <button
                      className="flex items-center justify-center w-[42px] h-[42px] bg-gradient-to-r from-[#00b8f1] to-[#0060f1] text-white rounded-lg hover:shadow-lg hover:shadow-[#00b8f1]/30 transition-all duration-300"
                      style={{
                        backgroundImage: colors.gradient,
                      }}
                    >
                      <Download
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        style={{ color: "#fff" }}
                      />
                      {/* <span className="ml-2 text-sm font-semibold">
                        Download
                      </span> */}
                    </button>
                  </Dropdown>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Charts Container */}
        <div className="space-y-6">
          {loading ? (
            <div
              className="h-64 flex items-center justify-center rounded-xl border"
              style={{
                backgroundColor: colors.cardBg,
                borderColor: colors.border,
              }}
            >
              <Spin size="large" />
            </div>
          ) : myData.length > 0 && chartsToRender[0]?.length > 0 ? (
            chartsToRender.map((chartData, idx) => {
              const categoryName = chartData[0]?.catName || "Usage";
              const districtName =
                selectedDistrict?.selectedDistrict || "District";

              // Clean the name for the file system
              const sanitizedCat = categoryName.replace(/\s+/g, "_");
              const accountLines = Object.keys(chartData[0] || {}).filter(
                (k) => k !== "date" && k !== "catName",
              );

              // Individual Download Menu for Category Mode
              const individualMenu = [
                {
                  key: "1",
                  label: (
                    <span onClick={() => downloadImage(idx)}>Download PNG</span>
                  ),
                },
                {
                  key: "2",
                  label: (
                    <CSVLink
                      data={chartData}
                      // filename={`${tabName}_${selectedDistrict?.selectedDistrict}.csv`}
                      filename={`${tabName}_${sanitizedCat}_${districtName}.csv`}
                    >
                      Download CSV
                    </CSVLink>
                  ),
                },
              ];

              return (
                <motion.div
                  key={idx}
                  ref={(el) => (chartContainerRefs.current[idx] = el)}
                  className="backdrop-blur-sm rounded-xl border p-4 sm:p-6 max-[767px]:overflow-x-scroll"
                  style={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h4
                      className="font-bold text-sm"
                      style={{ color: colors.primary }}
                    >
                      {/* {categoryEnabled
                        ? `${chartData[0]?.catName} Usage`
                        : "Water Usage"} */}
                    </h4>

                    {/* Individual Download - ONLY SHOW IF CATEGORY IS ON */}
                    {categoryEnabled && (
                      <Dropdown
                        menu={{ items: individualMenu }}
                        trigger={["click"]}
                      >
                        <button
                          className="flex items-center justify-center w-[36px] h-[36px] bg-gradient-to-r from-[#00b8f1] to-[#0060f1] text-white rounded-lg hover:shadow-lg hover:shadow-[#00b8f1]/30 transition-all duration-300"
                          style={{
                            backgroundImage: colors.gradient,
                          }}
                        >
                          <Download
                            className="w-4 h-4"
                            style={{ color: "#fff" }}
                          />
                        </button>
                      </Dropdown>
                    )}
                  </div>

                  <div className="h-[500px] min-[1921px]:h-[900px] max-[767px]:w-[600px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={colors.border}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="date"
                          stroke={colors.textSecondary}
                          tick={{ fontSize: 10 }}
                          // tickFormatter={(v) =>
                          //   moment(v, "dddd, MMM DD, YYYY hh:mm A").format(
                          //     "MMM DD",
                          //   )
                          // }
                          tickFormatter={(v) => {
                            if (!v) return "";
                            const m = moment(v);
                            if (!m.isValid()) return v; // Fallback to raw string if parsing fails

                            if (intervalLabel === "Months") {
                              return m.format("MMM YYYY"); // e.g., "Jan 2026"
                            }
                            return m.format("MMM DD");
                          }}
                        />
                        <YAxis
                          stroke={colors.textSecondary}
                          tick={{ fontSize: 10 }}
                          tickFormatter={(value) =>
                            value.toLocaleString("en-US")
                          }
                        >
                          <Label
                            value="Gallons"
                            angle={-90}
                            position="insideLeft"
                            style={{ fill: colors.textSecondary, fontSize: 12 }}
                          />
                        </YAxis>
                        <Tooltip
                          formatter={(value, name) => [
                            Number(value).toLocaleString("en-US"), // Format the value to locale string
                            name,
                          ]}
                          // contentStyle={{
                          //   backgroundColor: colors.background, // Dynamic background color based on theme
                          //   border: `1px solid ${colors.border}`, // Dynamic border color
                          //   borderRadius: "8px",
                          //   color: colors.text, // Dynamic text color based on theme
                          // }}
                          contentStyle={{
                            backgroundColor:
                              theme === "white" ? "#ffffff" : colors.background,
                            border: `1px solid ${colors.primary}30`,
                            borderRadius: "8px",
                            boxShadow: `0 8px 32px ${colors.primary}30`,
                            color: colors.text,
                          }}
                        />
                        <Legend
                          wrapperStyle={{
                            fontSize: "12px",
                            paddingTop: "15px",
                          }}
                        />

                        {/* Show Bars if state is 'bar' */}
                        {chartType === "bar" &&
                          accountLines.map((line, cIdx) => {
                            const strokeColor =
                              userColors[cIdx] ||
                              colors.chartColors?.[
                                cIdx % colors.chartColors?.length
                              ] ||
                              colors.primary;
                            return (
                              <Bar
                                key={`bar-${line}`}
                                dataKey={line}
                                stackId="a"
                                fill={strokeColor}
                                fillOpacity={0.9}
                                radius={[2, 2, 0, 0]}
                              />
                            );
                          })}

                        {/* Show Lines if state is 'line' */}
                        {chartType === "line" &&
                          accountLines.map((line, cIdx) => {
                            const strokeColor =
                              userColors[cIdx] ||
                              colors.chartColors?.[
                                cIdx % colors.chartColors?.length
                              ] ||
                              colors.primary;
                            return (
                              <Line
                                key={`line-${line}`}
                                type="monotone"
                                dataKey={line}
                                stroke={strokeColor}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                            );
                          })}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div
              className="h-64 flex flex-col items-center justify-center rounded-xl border text-sm"
              style={{
                backgroundColor: colors.cardBg,
                borderColor: colors.border,
                color: colors.textSecondary,
              }}
            >
              <p>
                {myData.length > 0
                  ? "No data for this selection."
                  : "Select accounts to view usage."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
